import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconButton,
  useSnackbar,
} from '@c2fo/liquidity';
import { EllipsisVerticalIcon } from '@c2fo/liquidity/icons';
import {
  useGlobalPriceDiscoveryOffer,
  useGlobalVariableRateOffer,
  useGlobalFixedRateOffer,
} from '@/enterprise/data/useGlobalOffer';
import { TakerMarketGroupType } from '@/enterprise/data/useGroupByPricingType';
import useTakerMarketsGroups from '@/enterprise/data/useTakerMarketsGroups';
import SetGlobalOffer from '@/enterprise/features/globalOffers/SetGlobalOffer';
import SetGlobalParticipation from '@/enterprise/features/globalOffers/SetGlobalParticipation';
import { trackParticipationMarketEvents } from '@/enterprise/features/globalOffers/utils';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import useExchangeRates from '@/shared/data/useExchangeRates';
import useRestrictions from '@/utils/useRestrictions';

export type GlobalParticipationTypes = Extract<TakerMarketGroupType, 'NAME_YOUR_RATE' | 'BENCHMARK' | 'FIXED'>;

export const RecurringRulesMenuItem = ({ onClick, readOnly }: { onClick: () => void; readOnly: boolean }) => {
  const { t } = useTranslation();

  const menuItemContent = {
    content: <div>{t('automation.modifyInvoiceRules')}</div>,
    label: t('automation.modifyInvoiceRules'),
  };

  return readOnly ? (
    <DropdownMenuItem aria-label={menuItemContent.label} disabled>
      {menuItemContent.content}
    </DropdownMenuItem>
  ) : (
    <DropdownMenuItem aria-label={menuItemContent.label} onClick={onClick}>
      {menuItemContent.content}
    </DropdownMenuItem>
  );
};

const GlobalOfferMenuItem = ({ onClick, readOnly }: { onClick: () => void; readOnly: boolean }) => {
  const { t } = useTranslation();

  // If user does not have permission to edit offers, disable the Modify Rate button
  return readOnly ? (
    <DropdownMenuItem disabled>{t('globalActions.modifyRate')}</DropdownMenuItem>
  ) : (
    <DropdownMenuItem onClick={onClick}>{t('globalActions.modifyRate')}</DropdownMenuItem>
  );
};

const GlobalParticipationMenuItems = ({
  onParticipationOffClick,
  onParticipationOnClick,
  readOnly,
}: {
  onParticipationOffClick: () => void;
  onParticipationOnClick: () => void;
  readOnly: boolean;
}) => {
  const { t } = useTranslation();

  // If user does not have permission to edit offers, disable the Turn All Participation On and Turn All Participation Off buttons
  return readOnly ? (
    <>
      <DropdownMenuItem disabled>{t('globalActions.turnParticipationOn')}</DropdownMenuItem>
      <DropdownMenuItem disabled>{t('globalActions.turnParticipationOff')}</DropdownMenuItem>
    </>
  ) : (
    <>
      <DropdownMenuItem onClick={onParticipationOnClick}>{t('globalActions.turnParticipationOn')}</DropdownMenuItem>
      <DropdownMenuItem onClick={onParticipationOffClick}>{t('globalActions.turnParticipationOff')}</DropdownMenuItem>
    </>
  );
};

const TabActionsDropdown = ({ tab }: { tab: TakerMarketGroupType }) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { data: exchangeRates } = useExchangeRates();
  const showSnackbar = useSnackbar();
  const takerMarketsGroups = useTakerMarketsGroups();
  const { getRestrictions } = useRestrictions();
  const { canEditOffers } = getRestrictions(takerMarketsGroups[tab].takerMarkets);
  const [showSetGlobalOfferModal, setShowSetGlobalOfferModal] = useState<boolean>(false);
  const [showSetGlobalParticipationModal, setShowSetGlobalParticipationModal] = useState<boolean>(false);

  // Mutations for each offer type for global participation off
  const { mutateAsync: toggleGlobalPriceDiscoveryParticipationOff } = useGlobalPriceDiscoveryOffer(
    takerMarketsGroups.NAME_YOUR_RATE.takerMarkets
  );
  const { mutateAsync: toggleGlobalVariableRateParticipationOff } = useGlobalVariableRateOffer(
    takerMarketsGroups.BENCHMARK.takerMarkets
  );
  const { mutateAsync: toggleGlobalFixedRateParticipationOff } = useGlobalFixedRateOffer(
    takerMarketsGroups.FIXED.takerMarkets
  );

  const turnGlobalParticipationOff = async () => {
    if (!canEditOffers) {
      return;
    }

    const submitValues = { isEnabled: false };
    const submissionBatchId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const groupTakerMarkets = takerMarketsGroups[tab].takerMarkets;
    let succeeded = false;

    const handleSuccessfulSubmission = () => {
      succeeded = true;
      trackParticipationMarketEvents({
        takerMarkets: groupTakerMarkets,
        pricingType: tab as GlobalParticipationTypes,
        isEnabled: false,
        exchangeRates,
        submissionBatchId,
        trackEnterpriseEvent,
      });
    };

    const handleError = () => {
      showSnackbar({ message: t('buyerControls.participationUpdateError') });
    };

    // Submit the mutation for the specific offer type
    if (tab === 'NAME_YOUR_RATE') {
      await toggleGlobalPriceDiscoveryParticipationOff(submitValues, {
        onSuccess: handleSuccessfulSubmission,
        onError: handleError,
      });
    }

    if (tab === 'BENCHMARK') {
      await toggleGlobalVariableRateParticipationOff(submitValues, {
        onSuccess: handleSuccessfulSubmission,
        onError: handleError,
      });
    }

    if (tab === 'FIXED') {
      await toggleGlobalFixedRateParticipationOff(submitValues, {
        onSuccess: handleSuccessfulSubmission,
        onError: handleError,
      });
    }

    if (succeeded) {
      showSnackbar({ message: t('buyerControls.participationTurnedOff') });
      trackEnterpriseEvent('global-participation::submitted', {
        isEnabled: false,
        submissionBatchId,
        NAME_YOUR_RATE: tab === 'NAME_YOUR_RATE' ? groupTakerMarkets.length : 0,
        BENCHMARK: tab === 'BENCHMARK' ? groupTakerMarkets.length : 0,
        FIXED: tab === 'FIXED' ? groupTakerMarkets.length : 0,
      });
    }
  };

  const openSetGlobalOfferModal = () => {
    setShowSetGlobalOfferModal(true);
    trackEnterpriseEvent('global-offer::clicked');
  };

  const openSetGlobalParticipationModal = () => {
    setShowSetGlobalParticipationModal(true);
    trackEnterpriseEvent('global-participation::clicked', { type: tab });
  };

  const openTabActionsDropdown = () => {
    trackEnterpriseEvent('tab-actions::clicked', { type: tab });
  };

  return (
    <IntercomDataTarget target="tabActionsDropdown">
      <SetGlobalOffer
        onClose={() => setShowSetGlobalOfferModal(false)}
        open={showSetGlobalOfferModal}
        takerMarkets={takerMarketsGroups[tab].takerMarkets}
      />
      {(tab === 'NAME_YOUR_RATE' || tab === 'BENCHMARK' || tab === 'FIXED') && (
        <SetGlobalParticipation
          onClose={() => setShowSetGlobalParticipationModal(false)}
          open={showSetGlobalParticipationModal}
          takerMarkets={takerMarketsGroups[tab].takerMarkets}
        />
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={openTabActionsDropdown}>
          <IconButton icon={EllipsisVerticalIcon} name={t('takerMarketTabs.tabActions')} variant="primary" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" collisionPadding={24} avoidCollisions={false}>
          {/*
           * Only show Modify Rate for NAME_YOUR_RATE tab
           * If user does not have permission to edit offers, disable the Modify Rate button
           */}
          {tab === 'NAME_YOUR_RATE' && (
            <GlobalOfferMenuItem onClick={openSetGlobalOfferModal} readOnly={!canEditOffers} />
          )}
          <GlobalParticipationMenuItems
            onParticipationOffClick={turnGlobalParticipationOff}
            onParticipationOnClick={openSetGlobalParticipationModal}
            readOnly={!canEditOffers}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </IntercomDataTarget>
  );
};

export default TabActionsDropdown;
